import React, { useState, useEffect, useCallback } from 'react';
import { 
  SearchIcon,
  FilterIcon,
  CurrencyDollarIcon,
  RefreshIcon,
  TrendingUpIcon,
  EyeIcon,
  ClockIcon,
  SparklesIcon,
  LightningBoltIcon,
  ChartBarIcon
} from '@heroicons/react/outline';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase-config';
import RFQCard from '../components/RFQCard';

const MarketFeed = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [budgetFilter, setBudgetFilter] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    highValue: 0,
    urgent: 0,
    recent: 0
  });

  const loadMarketFeed = useCallback(async () => {
    setLoading(true);
    try {
      // Use the same "posts" collection and query structure as PublicRFQFeed
      const q = query(
        collection(db, 'posts'),
        where('approved', '==', true),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      const querySnapshot = await getDocs(q);
      const rfqData = [];
      const categorySet = new Set();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Extract category from the data
        const category = data.categories && data.categories.length > 0 
          ? data.categories[0] 
          : data.channel || 'General';
        
        // Add category to set for unique categories
        if (category && category !== 'General') {
          categorySet.add(category);
        }
        
        // Map the fields correctly based on twerceapp structure
        rfqData.push({
          id: doc.id,
          textContent: data.textContent,
          title: data.textContent ? data.textContent.substring(0, 100) + (data.textContent.length > 100 ? '...' : '') : 'Untitled RFQ',
          description: data.textContent || 'No description available',
          categories: data.categories || [category],
          channel: data.channel,
          budget: data.budget || 0,
          location: data.location ? (typeof data.location === 'object' ? data.location.name : data.location) : 'Location not specified',
          authorName: data.authorName || 'Anonymous',
          author: data.author,
          timestamp: data.timestamp,
          createdAt: data.timestamp,
          urgency: 'medium', // Default since not stored in posts
          replyCount: data.replyCount || 0,
          media: data.media || []
        });
      });
      
      // Sort by date if available
      rfqData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setRfqs(rfqData);
      setLastUpdated(new Date());
      
      // Calculate stats
      const highValue = rfqData.filter(rfq => {
        const budget = typeof rfq.budget === 'number' ? rfq.budget : parseFloat(rfq.budget?.toString().replace(/[^0-9.]/g, '') || '0');
        return budget >= 50000;
      }).length;
      
      const urgent = rfqData.filter(rfq => rfq.urgency === 'high').length;
      const recent = rfqData.filter(rfq => {
        const date = new Date(rfq.timestamp);
        const now = new Date();
        const diffHours = (now - date) / (1000 * 60 * 60);
        return diffHours <= 24;
      }).length;
      
      setStats({
        total: rfqData.length,
        highValue,
        urgent,
        recent
      });
      
      // Extract unique categories from the fetched RFQs
      const uniqueCategories = ['all', ...Array.from(categorySet).sort()];
      setCategories(uniqueCategories);
      
    } catch (error) {
      console.error('Error loading market feed:', error);
    } finally {
    setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMarketFeed();
  }, [loadMarketFeed]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAutoRefreshing(true);
      loadMarketFeed().finally(() => {
        setIsAutoRefreshing(false);
      });
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [loadMarketFeed]);

  const filteredRFQs = rfqs.filter(rfq => {
    const rfqTitle = rfq.textContent || rfq.title || '';
    const rfqDescription = rfq.textContent || rfq.description || '';
    const rfqCategories = rfq.categories || [rfq.channel || 'General'];
    
    const matchesSearch = rfqTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfqDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfqCategories.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = categoryFilter === 'all' || 
                           rfqCategories.includes(categoryFilter) || 
                           rfq.channel === categoryFilter;
    
    // Budget filtering logic - handle different budget formats
    let matchesBudget = true;
    if (budgetFilter !== 'all' && rfq.budget) {
      const budgetValue = typeof rfq.budget === 'number' ? rfq.budget : 
                         parseFloat(rfq.budget.toString().replace(/[^0-9.]/g, ''));
      
      if (!isNaN(budgetValue)) {
      switch (budgetFilter) {
        case 'low':
            matchesBudget = budgetValue <= 10000;
          break;
        case 'medium':
            matchesBudget = budgetValue >= 10000 && budgetValue <= 50000;
          break;
        case 'high':
            matchesBudget = budgetValue >= 50000;
          break;
          default:
            matchesBudget = true;
        }
      }
    }
    
    return matchesSearch && matchesCategory && matchesBudget;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading market opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      {/* Enhanced Header */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-6 lg:mb-0">
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full text-sm font-medium mb-4 border border-orange-200">
                  <TrendingUpIcon className="w-4 h-4 mr-2" />
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
                  Live Market Feed
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-gray-900 via-orange-900 to-gray-900 bg-clip-text text-transparent">
              Market Feed
            </h1>
                <p className="text-xl text-gray-600 mb-4">
                  Discover procurement opportunities and connect with buyers
            </p>
                {lastUpdated && (
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="w-4 h-4 mr-2" />
                    <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                    {isAutoRefreshing && (
                      <span className="ml-3 text-orange-600 font-medium">
                        <RefreshIcon className="h-4 w-4 inline animate-spin mr-1" />
                        Refreshing...
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              <button
                onClick={loadMarketFeed}
                disabled={loading}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold"
              >
                <RefreshIcon className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Feed
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Opportunities</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">High Value</p>
                <p className="text-2xl font-bold text-gray-900">{stats.highValue}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                <LightningBoltIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Urgent</p>
                <p className="text-2xl font-bold text-gray-900">{stats.urgent}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Recent (24h)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.recent}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
              <FilterIcon className="w-5 h-5 mr-2 text-orange-600" />
              Filter Opportunities
            </h2>
            <p className="text-gray-600">Find the perfect opportunities for your business</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Enhanced Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Search Opportunities</label>
              <div className="relative">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, description, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-200"
                />
              </div>
            </div>

            {/* Enhanced Category Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Category</label>
              <div className="relative">
                <FilterIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-200 appearance-none"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Enhanced Budget Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Budget Range</label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={budgetFilter}
                  onChange={(e) => setBudgetFilter(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 hover:border-orange-200 appearance-none"
                >
                  <option value="all">All Budgets</option>
                  <option value="low">Under ₦10K</option>
                  <option value="medium">₦10K - ₦50K</option>
                  <option value="high">Over ₦50K</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced RFQs Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-orange-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                  <EyeIcon className="w-6 h-6 mr-3 text-orange-600" />
                  Available Opportunities
                </h2>
                <p className="text-gray-600">
              {filteredRFQs.length} opportunity{filteredRFQs.length !== 1 ? 'ies' : ''} found
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>
              
              {filteredRFQs.length > 0 && (
                <div className="flex items-center text-sm text-gray-500">
                  <TrendingUpIcon className="w-4 h-4 mr-2 text-green-500" />
                  <span>Live updates every 30 seconds</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-8">
            {filteredRFQs.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                  <SearchIcon className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  No opportunities found
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Try adjusting your search criteria or filters to find more opportunities
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter('all');
                      setBudgetFilter('all');
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-semibold"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={loadMarketFeed}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                  >
                    Refresh Feed
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredRFQs.map((rfq) => (
                  <RFQCard key={rfq.id} rfq={rfq} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketFeed; 